import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { chatFeedbackApi, TurnRating } from '../../../services/api/chatFeedback.api';
import { useClickOutside } from '../../../hooks/useClickOutside';

export type { TurnRating };

const UP_PHRASES = ['Accurate', 'Helpful', 'Clear & well formatted'];
const DOWN_PHRASES = ['Inaccurate', 'Not helpful', 'Incomplete or missing info'];

interface TurnFeedbackProps {
  turnId: string;
  sessionId: string | null;
  // The rating already on record for this turn, lifted up to Chat.tsx so it
  // survives history pagination/scrolling within the current page session —
  // see Chat.tsx's turnRatings map for why this isn't local state here.
  rating?: TurnRating;
  onRated: (turnId: string, rating: TurnRating) => void;
}

const TurnFeedback: React.FC<TurnFeedbackProps> = ({ turnId, sessionId, rating, onRated }) => {
  const [openPanel, setOpenPanel] = useState<TurnRating | null>(null);
  const [selectedPhrases, setSelectedPhrases] = useState<string[]>([]);
  const [customText, setCustomText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // wrapRef holds both thumb buttons and the panel, so the thumbs count as inside
  useClickOutside([wrapRef], () => setOpenPanel(null), openPanel !== null);

  const togglePanel = (nextRating: TurnRating) => {
    setOpenPanel((prev) => (prev === nextRating ? null : nextRating));
    setSelectedPhrases([]);
    setCustomText('');
  };

  const togglePhrase = (phrase: string) => {
    setSelectedPhrases((prev) =>
      prev.includes(phrase) ? prev.filter((p) => p !== phrase) : [...prev, phrase]
    );
  };

  const handleSubmit = async () => {
    if (!openPanel || submitting) return;

    if (!sessionId) {
      toast.error('Something went wrong, please try again!');
      return;
    }

    setSubmitting(true);

    try {
      await chatFeedbackApi.submitTurnFeedback({
        turn_id: turnId,
        session_id: sessionId,
        chat_response_rating: openPanel,
        feedback_defined_phrases: selectedPhrases,
        custom_feedback: customText.trim() || null,
      });
      onRated(turnId, openPanel);
      setOpenPanel(null);
      toast.success('Thanks for the feedback!');
    } catch {
      toast.error('Something went wrong, please try again!');
    } finally {
      setSubmitting(false);
    }
  };

  const phrases = openPanel === 'down' ? DOWN_PHRASES : UP_PHRASES;

  return (
    <div className="turn-feedback" ref={wrapRef}>
      <button
        type="button"
        className={`msg-action-btn${rating === 'up' ? ' msg-action-btn--rated-up' : ''}`}
        aria-label="Good response"
        title="Good response"
        onClick={() => togglePanel('up')}
      >
        <i className="fas fa-thumbs-up" />
      </button>
      <button
        type="button"
        className={`msg-action-btn${rating === 'down' ? ' msg-action-btn--rated-down' : ''}`}
        aria-label="Bad response"
        title="Bad response"
        onClick={() => togglePanel('down')}
      >
        <i className="fas fa-thumbs-down" />
      </button>

      {openPanel && (
        <div className="turn-feedback-popover">
          <p className="turn-feedback-popover-title">
            {openPanel === 'up' ? 'What did you like?' : 'What went wrong?'}
          </p>

          <div className="turn-feedback-chips">
            {phrases.map((phrase) => (
              <button
                key={phrase}
                type="button"
                className={`turn-feedback-chip${selectedPhrases.includes(phrase) ? ' selected' : ''}`}
                onClick={() => togglePhrase(phrase)}
              >
                {phrase}
              </button>
            ))}
          </div>

          <textarea
            className="turn-feedback-textarea"
            placeholder="Add more details (optional)"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            maxLength={2000}
          />

          <div className="turn-feedback-actions">
            <button
              type="button"
              className="turn-feedback-action-btn"
              onClick={() => setOpenPanel(null)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="turn-feedback-action-btn primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TurnFeedback;
